import { SEO } from '../components/SEO';
import { Shield, Mail, Phone, MapPin } from 'lucide-react';

export const Privacy = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="privacy-page">
      <SEO 
        title="Privacy Policy" 
        description="Surfside Patrol Privacy Policy. Learn how we collect, use, and protect your personal information. Your privacy is important to us."
      />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 bg-[#121212]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-4">
            <Shield className="h-8 w-8 text-[#F5C518] mr-3" />
            <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest">
              Legal
            </p>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-tighter text-white mb-4 font-['Barlow_Condensed']">
            Privacy <span className="text-[#F5C518]">Policy</span>
          </h1>
          <p className="text-zinc-500 text-sm">
            Last updated: {currentDate}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-invert max-w-none">
            
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Introduction
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Surfside Patrol ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our security services.
              </p>
              <p className="text-zinc-400 leading-relaxed">
                Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Information We Collect
              </h2>
              
              <h3 className="text-lg font-semibold text-[#F5C518] mb-3 mt-6">Personal Information</h3>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We may collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-4 ml-4">
                <li>Fill out our contact form</li>
                <li>Request a quote for our services</li>
                <li>Subscribe to our newsletter</li>
                <li>Contact us via phone or email</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mb-4">
                This information may include:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 ml-4">
                <li>Name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Business or property address</li>
                <li>Type of security service needed</li>
                <li>Any additional information you choose to provide</li>
              </ul>

              <h3 className="text-lg font-semibold text-[#F5C518] mb-3 mt-6">Automatically Collected Information</h3>
              <p className="text-zinc-400 leading-relaxed">
                When you visit our website, we may automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the cookies that are installed on your device.
              </p>
            </div>

            {/* How We Use Your Information */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                How We Use Your Information
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 ml-4">
                <li>Respond to your inquiries and provide customer service</li>
                <li>Process and fulfill your service requests</li>
                <li>Send you information about our services that may be of interest to you</li>
                <li>Improve our website and services</li>
                <li>Comply with legal obligations</li>
                <li>Protect against fraudulent or illegal activity</li>
              </ul>
            </div>

            {/* Information Sharing */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Information Sharing & Disclosure
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 ml-4">
                <li><strong className="text-white">Service Providers:</strong> With third-party vendors who assist us in operating our website and conducting our business</li>
                <li><strong className="text-white">Legal Requirements:</strong> When required by law or to respond to legal process</li>
                <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong className="text-white">Protection:</strong> To protect our rights, privacy, safety, or property</li>
              </ul>
            </div>

            {/* Data Security */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Data Security
              </h2>
              <p className="text-zinc-400 leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>

            {/* Cookies */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Cookies & Tracking Technologies
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to track activity on our website and hold certain information. Cookies are files with a small amount of data that are sent to your browser from a website and stored on your device.
              </p>
              <p className="text-zinc-400 leading-relaxed">
                You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our website.
              </p>
            </div>

            {/* Your Rights */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Your Privacy Rights
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Depending on your location, you may have certain rights regarding your personal information, including:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 ml-4">
                <li>The right to access your personal information</li>
                <li>The right to correct inaccurate information</li>
                <li>The right to request deletion of your information</li>
                <li>The right to opt-out of marketing communications</li>
                <li>The right to data portability</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mt-4">
                To exercise any of these rights, please contact us using the information provided below.
              </p>
            </div>

            {/* California Residents */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                California Privacy Rights
              </h2>
              <p className="text-zinc-400 leading-relaxed">
                If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA). You have the right to request disclosure of information we collect, request deletion of your personal information, and opt-out of the sale of your personal information. We do not sell personal information.
              </p>
            </div>

            {/* Children's Privacy */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Children's Privacy
              </h2>
              <p className="text-zinc-400 leading-relaxed">
                Our website and services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </div>

            {/* Changes to Policy */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Changes to This Policy
              </h2>
              <p className="text-zinc-400 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </div>

            {/* Contact Us */}
            <div className="bg-[#121212] border border-zinc-800 p-8 mt-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
                Contact Us
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-6">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="space-y-4">
                <div className="flex items-center text-zinc-400">
                  <Phone className="h-5 w-5 text-[#F5C518] mr-3" />
                  <a href="tel:714-588-2460" className="hover:text-[#F5C518] transition-colors">714-588-2460</a>
                </div>
                <div className="flex items-center text-zinc-400">
                  <Mail className="h-5 w-5 text-[#F5C518] mr-3" />
                  <a href="mailto:surfside.josh4995@gmail.com" className="hover:text-[#F5C518] transition-colors">surfside.josh4995@gmail.com</a>
                </div>
                <div className="flex items-start text-zinc-400">
                  <MapPin className="h-5 w-5 text-[#F5C518] mr-3 mt-0.5" />
                  <span>2102 Business Center Dr<br />Irvine, CA 92612</span>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <p className="text-[#F5C518] font-bold">Surfside Patrol Private Security</p>
                <p className="text-zinc-500 text-sm">PPO #15982</p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};
